import { queryOptions, useQuery } from '@tanstack/react-query'
import { isEmpty, isUndefined, omitBy } from 'lodash'

import {
  ContactV2Response,
  LegalEntityListRequestV3,
  LegalEntityListResponseV3,
  LegalEntityResponseV3,
} from '@forto-dev/customer-master-data-models'
import { SelectProps } from '@forto-dev/phoenix-ui-core'

import Client from '@/api/client'
import { getConfig } from '@/app/config'

import { formatContactLabel } from '../utils/formatUtils'

type ContactV2PaginationResponse = { items: ContactV2Response[]; total: number }

const basePath = '/api/customer-master-data'
const client = new Client(getConfig().API_ENDPOINT ?? '')

export function getLegalEntities(params: LegalEntityListRequestV3 = {}) {
  return client.get<LegalEntityListResponseV3>(`${basePath}/v3/legal-entities`, {
    params: omitBy(params, isUndefined),
  })
}

function getContactsForLegalEntities(legalEntity?: string, query?: string) {
  return client.get<ContactV2PaginationResponse>(`${basePath}/contacts`, {
    params: { legalEntity: [legalEntity], search: query },
  })
}

export const getLegalEntityDetails = (legalEntityCode: string) => {
  return client.get<LegalEntityResponseV3>(`${basePath}/v3/legal-entities/${legalEntityCode}`)
}

const getContactDetailsByID = (contactID: string) => {
  return client.get<ContactV2Response>(`${basePath}/contacts/${contactID}`)
}

export const useContactDetailsQuery = (contactID: string | null = '', enabled = true) => {
  return useQuery({
    queryKey: ['contact', contactID],
    queryFn: () => getContactDetailsByID(contactID as string),
    enabled: enabled && !isEmpty(contactID),
  })
}

export const getLegalEntityDetailsQueryOptions = (legalEntityCode: string = '', enabled = true) => {
  return queryOptions({
    queryKey: ['legalEntity', legalEntityCode],
    queryFn: () => getLegalEntityDetails(legalEntityCode),
    enabled: enabled && !isEmpty(legalEntityCode),
  })
}

export const useLegalEntityDetailsQuery = (legalEntityCode: string = '', enabled = true) => {
  return useQuery(getLegalEntityDetailsQueryOptions(legalEntityCode, enabled))
}

export const useContactsQuery = (legalEntity?: string, query?: string) => {
  return useQuery({
    queryKey: ['contactsForLegalEntity', legalEntity, query],
    enabled: legalEntity != null,
    queryFn: () => getContactsForLegalEntities(legalEntity, query),
  })
}

export const useLegalEntitiesOptions = (params: LegalEntityListRequestV3 = {}) => {
  return useQuery({
    queryKey: ['legalEntities', params],
    queryFn: async () => {
      const result = await getLegalEntities(params)
      return (result.items ?? []).map((item) => ({
        value: item.code,
        label: item.name,
      }))
    },
  })
}

export const useContactsOptions = (legalEntityID?: string, query?: string) => {
  const { data, ...queryResult } = useContactsQuery(legalEntityID, query)

  const contactItems: SelectProps['items'] = (data?.items ?? []).map((item) => ({
    value: item.id,
    label: formatContactLabel(item) ?? '',
  }))

  return { data: contactItems, ...queryResult }
}
